import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type ButtonHTMLAttributes,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Link} from 'react-router-dom';
import css from './common.module.css';
import {Icon, type IconName} from './Icon';
import {classNames} from '../../utils';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: css.btnPrimary,
  secondary: css.btnSecondary,
  ghost: css.btnGhost,
  danger: css.btnDanger,
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: IconName;
  loading?: boolean;
}

export function Button({
  variant = 'secondary',
  icon,
  loading,
  children,
  disabled,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={classNames(VARIANT_CLASS[variant], className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <span className={css.spinner} />
      ) : (
        icon && <Icon name={icon} size={16} />
      )}
      {children}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  size?: number;
}

export function IconButton({icon, size = 18, ...rest}: IconButtonProps) {
  return (
    <button className={css.iconBtn} type="button" {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

export function Spinner({large}: {large?: boolean}) {
  return <span className={large ? css.spinnerLarge : css.spinner} />;
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}

export function Modal({open, onClose, children, width}: ModalProps) {
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }
  return (
    <div
      className={classNames(css.overlay, 'no-print')}
      onMouseDown={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={css.modal}
        style={width ? {width: `min(${width}px, calc(100vw - 48px))`} : undefined}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}

export const ModalTitle = ({children}: {children: ReactNode}) => (
  <h2 className={css.modalTitle}>{children}</h2>
);
export const ModalText = ({children}: {children: ReactNode}) => (
  <p className={css.modalText}>{children}</p>
);
export const ModalActions = ({children}: {children: ReactNode}) => (
  <div className={css.modalActions}>{children}</div>
);

interface MenuProps {
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
}

/** Click-to-open dropdown anchored to its trigger. */
export function Menu({trigger, children}: MenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className={css.menuWrap} ref={wrapRef}>
      <div onClick={() => setOpen(v => !v)}>{trigger}</div>
      {open && <div className={css.menu}>{children(() => setOpen(false))}</div>}
    </div>
  );
}

export function MenuItem({
  icon,
  danger,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: IconName;
  danger?: boolean;
}) {
  return (
    <button
      className={danger ? css.menuItemDanger : css.menuItem}
      type="button"
      {...rest}
    >
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
}

export function Markdown({children}: {children: string}) {
  return (
    <div className="markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Assistant answers cite notes as [title](owlly://note/{id}) —
          // route those to the in-app note page; open real URLs externally.
          a: ({href, children: linkChildren}) => {
            const noteMatch = href?.match(/^owlly:\/\/note\/([\w-]+)/);
            if (noteMatch) {
              return <Link to={`/notes/${noteMatch[1]}`}>{linkChildren}</Link>;
            }
            return (
              <a href={href} target="_blank" rel="noreferrer">
                {linkChildren}
              </a>
            );
          },
        }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
